import { privateApi } from '@/config';
import { Quote, QuoteFilters, CreateQuoteRequest, UpdateQuoteRequest, QuoteListResponse, QuoteMetrics } from '../types/quote.types';

/** `GET /agency/quotes` (apps/api/src/modules/agency/controllers/quote.controller.ts). */
export const RUTA_LISTA_COTIZACIONES = '/agency/quotes';

/**
 * El selector de fecha da el día a las 00:00Z. El backend filtra con BETWEEN, así que mandar ese instante como
 * «hasta» deja fuera TODO el día final (el rango 02-oct → 02-oct daba 0 filas aunque la cotización era de ese día).
 */
export function finDelDia(d: Date): Date {
  const fin = new Date(d.getTime());
  fin.setUTCHours(23, 59, 59, 999);
  return fin;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** `clientId` del DTO es `@IsUUID` (quote.dto.ts:198-201): cualquier otro valor da 400. */
export const esUuid = (s: string): boolean => UUID_RE.test(s);

/**
 * El cuadro «Cliente» de la pantalla es de TEXTO libre («Buscar por cliente…»), no un uuid. Devuelve lo que el usuario
 * escribió cuando NO es un uuid (y por lo tanto no puede viajar como `clientId`); '' si está vacío o es un uuid.
 */
function textoDeCliente(filters?: Partial<QuoteFilters>): string {
  const c = (filters?.clientId ?? '').trim();
  return c && !esUuid(c) ? c : '';
}

/**
 * El DTO tiene UN solo `search` (número, título o nombre del cliente). Si el usuario llenó «Búsqueda» Y «Cliente»,
 * viaja «Búsqueda» y el texto de «Cliente» se aplica acá, sobre el nombre del cliente de las filas que volvieron
 * (las dos condiciones a la vez, no una o la otra). Con una sola de las dos cajas llenas no hace nada.
 */
export function soloDelClienteEscrito<T extends { clientName?: string }>(quotes: T[], filters?: Partial<QuoteFilters>): T[] {
  const aca = (filters?.search ?? '').trim() ? textoDeCliente(filters) : '';
  if (!aca) return quotes;
  const q = aca.toLowerCase();
  return quotes.filter((x) => (x.clientName ?? '').toLowerCase().includes(q));
}

/** Los filtros de la pantalla → la query de `QuoteFilterDto` (lo neutro 'all' y lo vacío no se mandan). */
export function paramsListaCotizaciones(filters?: Partial<QuoteFilters>): Record<string, string | number> {
  const params: Record<string, string | number> = { page: 1, limit: 100 };
  // El texto de «Cliente» viaja como `search` (busca también por nombre del cliente), NUNCA como `clientId`: cada tecla
  // era un 400 y la pantalla caía en «Error al cargar los datos».
  const search = (filters?.search ?? '').trim() || textoDeCliente(filters);
  if (search) params.search = search;
  if (filters?.type && filters.type !== 'all') params.type = filters.type;
  if (filters?.status && filters.status !== 'all') params.status = filters.status;
  // Solo un uuid de verdad viaja como `clientId`.
  const cliente = (filters?.clientId ?? '').trim();
  if (cliente && esUuid(cliente)) params.clientId = cliente;
  // El backend filtra por fecha SOLO si vienen las dos (BETWEEN).
  if (filters?.dateRange?.from && filters?.dateRange?.to) {
    params.dateFrom = filters.dateRange.from.toISOString();
    params.dateTo = finDelDia(filters.dateRange.to).toISOString();
  }
  return params;
}

export const quotesApi = {
  /**
   * Obtener las cotizaciones (lista de `/agente/cotizaciones`).
   *
   * GH#2307: llamaba a una ruta de «mis cotizaciones» que el API NO tiene (`agency/quotes/:id` se la tragaba como si
   * fuera un uuid y daba 400). La lista real es `GET /agency/quotes`.
   *
   * LÍMITES que esta ruta trae y la anterior (inexistente) no: es de TODO el tenant (no filtra por agente) y solo la
   * abren AGENCY_ADMIN / REAL_ESTATE_AGENT_ADMIN / SUPER_ADMIN, no AGENCY_AGENT_USER. Sin `limit` el backend manda 10;
   * se pide el máximo (100) porque la pantalla pagina en el cliente sobre lo que carga.
   */
  async getMyQuotes(filters?: Partial<QuoteFilters>): Promise<QuoteListResponse> {
    const response = await privateApi.get(RUTA_LISTA_COTIZACIONES, { params: paramsListaCotizaciones(filters) });
    return response.data;
  },

  /**
   * Obtener cotización por ID
   */
  async getById(id: string): Promise<Quote> {
    const response = await privateApi.get(`/agency/quotes/${id}`);
    // La API devuelve { success: true, data: {...} }
    return response.data.data || response.data;
  },

  /**
   * Crear nueva cotización
   */
  async create(quoteData: CreateQuoteRequest): Promise<Quote> {
    const response = await privateApi.post('/agency/quotes', quoteData);
    return response.data;
  },

  /**
   * Actualizar cotización
   */
  async update(id: string, quoteData: UpdateQuoteRequest): Promise<Quote> {
    const response = await privateApi.patch(`/agency/quotes/${id}`, quoteData);
    return response.data;
  },

  /**
   * Enviar cotización al cliente
   */
  async send(id: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/send`);
    return response.data;
  },

  /**
   * Aceptar cotización
   * ⚠ GH#2307: el API NO tiene esta ruta (el controlador solo tiene send, approve, reject y duplicate). La pantalla
   * del dueño-agente ya no la llama; la dejo aquí solo porque `useAgentQuotes` y `quotesSaga` aún la referencian.
   */
  async accept(id: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/accept`);
    return response.data;
  },

  /**
   * Rechazar cotización
   */
  async reject(id: string, rejectionReason: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/reject`, {
      rejectionReason,
    });
    return response.data;
  },

  /**
   * Duplicar cotización
   */
  async duplicate(id: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/duplicate`);
    return response.data;
  },

  /**
   * Cancelar cotización enviada
   * ⚠ GH#2307: el API NO tiene esta ruta (el controlador solo tiene send, approve, reject y duplicate). La pantalla
   * del dueño-agente ya no la llama; la dejo aquí solo porque `useAgentQuotes` y `quotesSaga` aún la referencian.
   */
  async cancel(id: string, cancellationReason?: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/cancel`, {
      cancellationReason: cancellationReason || 'Cancelada por el agente',
    });
    return response.data;
  },

  /**
   * Reactivar cotización cancelada
   * ⚠ GH#2307: el API NO tiene esta ruta (el controlador solo tiene send, approve, reject y duplicate). La pantalla
   * del dueño-agente ya no la llama; la dejo aquí solo porque `useAgentQuotes` y `quotesSaga` aún la referencian.
   */
  async reactivate(id: string): Promise<Quote> {
    const response = await privateApi.post(`/agency/quotes/${id}/reactivate`);
    return response.data;
  },

  /**
   * Eliminar cotización
   */
  async delete(id: string): Promise<void> {
    await privateApi.delete(`/agency/quotes/${id}`);
  },

  /**
   * Obtener estadísticas de cotizaciones
   */
  async getStats(): Promise<QuoteMetrics> {
    const response = await privateApi.get('/agency/quotes/stats');
    return response.data;
  },
};

export default quotesApi;
